// The facts the server hands the Pulse Agent (ENG-3417, ENG-3418).
// Spec: docs/specs/pulse.md §7 "Facts are the server's". Every block is a
// section that fails on its own, so one broken query hides one block, never
// the whole answer. Null never means zero: a value the window could not
// measure is `null`, and every average or rate carries its sample size.
import { ReviewPlatform } from "../Reviews/Review.Interfaces";
import type { PulseEscalationThemes } from "./Pulse.Themes";

export enum PulseFactStatus {
  OK = "ok",
  ERROR = "error"
}

/** One independently-failing block of facts. `data` is present exactly when `status` is OK. */
export interface PulseFactSection<T> {
  status: PulseFactStatus;
  data?: T;
  /** Display-safe reason when `status` is ERROR ("unavailable"). Never a raw error. */
  error?: string;
}

/** A window of time, ISO strings: `from` inclusive, `to` exclusive. */
export interface PulseWindow {
  from: string;
  to: string;
}

/** A number and how much it rests on. `value` is null when nothing in the window could be measured. */
export interface PulseMeasure {
  value: number | null;
  sampleSize: number;
}

/** The three channels the Contact Center breaks out; any other channel string folds into web, as the CC does. */
export type PulseChannel = "web" | "sms" | "voice";

/** How Pulse names each channel: on cards, in answers and in AI Studio's catalog alike. */
export const PULSE_CHANNEL_LABELS: Readonly<Record<PulseChannel, string>> = { web: "Web chat", sms: "SMS", voice: "Voice" };

/** A channel's name; anything else folds into web, as the Contact Center does. */
export function pulseChannelLabel(channel: string | null | undefined): string {
  const known = (Object.keys(PULSE_CHANNEL_LABELS) as PulseChannel[]).find((candidate) => candidate === channel);
  return PULSE_CHANNEL_LABELS[known ?? "web"];
}

/** How Pulse names each review site. */
export const PULSE_PLATFORM_LABELS: Readonly<Record<ReviewPlatform, string>> = {
  [ReviewPlatform.GOOGLE]: "Google",
  [ReviewPlatform.YELP]: "Yelp",
  [ReviewPlatform.SPOT]: "SPOT",
  [ReviewPlatform.SWIVL]: "Swivl"
};

/** A review site's name: "Google"; one Pulse doesn't know reads as itself. */
export function pulsePlatformLabel(platform: string): string {
  const known = Object.values(ReviewPlatform).find((candidate) => candidate === platform);
  return known ? PULSE_PLATFORM_LABELS[known] : platform;
}

/** Locations a member may see for one kind of data. `"all"` = unrestricted; `[]` = none. */
export type PulseLocationReach = number[] | "all";

export interface PulseReach {
  conversations: PulseLocationReach;
  tickets: PulseLocationReach;
  reviews: PulseLocationReach;
  /** Lead counts: the CRM view permission (ENG-3225, Mason 6 October). */
  leads: PulseLocationReach;
}

export type PulseReachScope = keyof PulseReach;

// ─── Org facts ──────────────────────────────────────────────────────────────

export interface PulseChannelMetrics {
  channel: PulseChannel;
  /** Inbound (customer-started) conversations. */
  inbound: number;
  /** Mean health 0-100 over ended, scored conversations. */
  health: PulseMeasure;
  /** Escalation raised → agent accepted, seconds. */
  avgWaitSeconds: PulseMeasure;
  maxWaitSeconds: number | null;
  /** 0-1, unresolved or abandoned over everything judged. */
  abandonmentRate: PulseMeasure;
}

/** The Agent Dashboard's KPI figures for one window, with a sample size on every average and rate. */
export interface PulseMetricsWindow {
  window: PulseWindow;
  health: PulseMeasure;
  avgWaitSeconds: PulseMeasure;
  abandonmentRate: PulseMeasure;
  byChannel: PulseChannelMetrics[];
}

export interface PulseRosterMember {
  memberId: number;
  name: string;
  /** Live presence: online | busy | away | offline. */
  state: string;
  activeAssignmentCount: number;
}

export interface PulseActiveInteraction {
  botConversationId: number;
  channel: PulseChannel;
  /** `waiting` = an escalation is pending and no agent has taken it. */
  state: "engaged" | "waiting";
  handledByKind: "agent" | "ai" | "no_agents_accepted" | "no_agents_available";
  startedAt: string;
  /** When a human was first asked for (first offer or routing event wanting one). Null unless waiting. */
  waitingSince: string | null;
  /** Waiting rows only: the conversation's location; null when none was chosen (mostly web chat) or not waiting. */
  locationId: number | null;
  /**
   * Waiting rows only: when the offer out now stops ringing; null when nobody
   * is being offered it. While it rings, it's that person's to answer (ENG-3221
   * rider 5), so Pulse doesn't show it to anyone else.
   */
  ringingUntil: string | null;
}

export interface PulseChannelRate {
  channel: PulseChannel;
  /** 0-1; sampleSize is the denominator. */
  rate: PulseMeasure;
}

export interface PulseAcceptedShare {
  channel: PulseChannel;
  memberId: number;
  accepted: number;
  /** Every accepted conversation on this channel in the window, across agents. */
  channelAccepted: number;
}

export interface PulseLocationHealth {
  locationId: number;
  health: PulseMeasure;
}

/**
 * A conversation is "struggling" below this score — the product's own
 * Struggling band (`COACHING_SCORE_BANDS` in ../Coaching/Coaching.Interface.ts).
 */
export const PULSE_STRUGGLING_SCORE_BELOW = 40;

export interface PulseStrugglingConversation {
  botConversationId: number;
  score: number;
}

export interface PulseStrugglingAtLocation {
  locationId: number;
  count: number;
  /** Lowest score first, at most five. */
  examples: PulseStrugglingConversation[];
}

/** Live conversations; `capped` when the Contact Center's 200-row cap was reached. */
export interface PulseActiveInteractions {
  items: PulseActiveInteraction[];
  capped: boolean;
}

/** Share of inbound conversations that asked for a human, per channel. */
export interface PulseEscalationRates {
  today: PulseChannelRate[];
  baseline7d: PulseChannelRate[];
  /** The thirty whole days before today: the Head of Operations' monthly normal (ENG-3225). */
  baseline30d: PulseChannelRate[];
}

export interface PulseLocationHealthSummary {
  org: PulseMeasure;
  locations: PulseLocationHealth[];
}

export interface PulseLocationName {
  id: number;
  name: string;
}

/** Conversations split by who handled them — the Contact Center history's rule. */
export interface PulseHandledSplit {
  /** The AI handled it start to finish. */
  ai: number;
  /** An agent accepted it. */
  team: number;
  /** A person was asked for and nobody took it: offers went unaccepted, or routing found no one. */
  unanswered: number;
}

/** One local hour or day of finished customer conversations. */
export interface PulseActivityBucket extends PulseHandledSplit {
  /** The bucket's local start: `2026-09-30T09:00` for an hour, `2026-09-30` for a day. */
  key: string;
  /** How a chart names it: "9 AM", "Wed". */
  label: string;
}

/** The Contact Center's displayed topic (resolveInteractionTopic). */
export type PulseTopic = "Emergency" | "Sales" | "Service" | "Uncategorized";

export interface PulseTopicCount {
  topic: PulseTopic;
  count: number;
}

/** The dashboard's health for one local week (Monday start); null below the sample minimum. */
export interface PulseWeeklyHealth {
  weekStart: string;
  label: string;
  health: PulseMeasure;
}

/** One local week (Monday start), as a weekly block keys and names it: `2026-09-28`, "Sep 28". */
export interface PulseWeekKey {
  key: string;
  label: string;
}

/** One of the org's groups and its locations (ENG-3225). Every location belongs to exactly one group. */
export interface PulseGroupFact {
  id: number;
  name: string;
  locationIds: number[];
}

/** One location's health in one local week. The average isn't rounded, so a group's can be weighed from its locations'. */
export interface PulseLocationWeekHealth {
  locationId: number;
  weekStart: string;
  health: PulseMeasure;
}

/** Health per location per local week (ENG-3225): the weeks, oldest first, this one last, and a row for each location and week with any. */
export interface PulseLocationHealthWeeks {
  weeks: PulseWeekKey[];
  rows: PulseLocationWeekHealth[];
}

/** One location's reviews on one platform in one local week (ENG-3225). */
export interface PulseReviewWeek {
  locationId: number;
  platform: string;
  weekStart: string;
  reviews: number;
  /** The sum of their star ratings: divided by `reviews`, their average. */
  ratingSum: number;
  /** How many have no reply. */
  unreplied: number;
}

/** Reviews per location, platform and local week (ENG-3225): archived reviews aren't counted. */
export interface PulseReviewWeeks {
  weeks: PulseWeekKey[];
  rows: PulseReviewWeek[];
}

/** One location's leads in one local week (ENG-3225). */
export interface PulseLeadWeek {
  locationId: number;
  weekStart: string;
  leads: number;
}

/** Leads per location and local week (ENG-3225), dated when the lead was made, else when Swivl first saw it. */
export interface PulseLeadWeeks {
  weeks: PulseWeekKey[];
  rows: PulseLeadWeek[];
}

/** What the digest's chart and prose are drawn from (ENG-3220). */
export interface PulseActivity {
  /** The last 24 local hours to now, oldest first; an hour with nothing is zeros. */
  byHour24h: PulseActivityBucket[];
  /** The last 7 local days, today included, oldest first. */
  byDay7d: PulseActivityBucket[];
  /** Customer conversations in the last 24 hours by topic, largest first. */
  byTopic24h: PulseTopicCount[];
  /** The last 12 local weeks, this one included, oldest first. */
  weeklyHealth12w: PulseWeeklyHealth[];
}

export interface PulseOrgFacts {
  orgId: number;
  /** IANA timezone "today" is measured in. */
  timezone: string;
  /** True when the org timezone could not be resolved and UTC was used. */
  timezoneFallback: boolean;
  builtAt: string;
  windows: {
    /** Org-local midnight → now. */
    today: PulseWindow;
    /** The seven whole days before today. */
    baseline7d: PulseWindow;
    /** The thirty whole days before today. */
    baseline30d: PulseWindow;
    last24h: PulseWindow;
    /** The first of this month, org-local, → now: My Account's "this month". */
    monthToDate: PulseWindow;
  };
  /** The org's locations, so a card can name the site it is about. */
  locations: PulseFactSection<PulseLocationName[]>;
  metricsToday: PulseFactSection<PulseMetricsWindow>;
  metricsBaseline7d: PulseFactSection<PulseMetricsWindow>;
  /** The same figures over the thirty days before today — the slower, monthly normal. */
  metricsBaseline30d: PulseFactSection<PulseMetricsWindow>;
  /** The same figures this month so far, for My Account. */
  metricsMonthToDate: PulseFactSection<PulseMetricsWindow>;
  /** Finished customer conversations this month so far, split by who handled them. */
  handledMonthToDate: PulseFactSection<PulseHandledSplit>;
  roster: PulseFactSection<PulseRosterMember[]>;
  active: PulseFactSection<PulseActiveInteractions>;
  escalationRates: PulseFactSection<PulseEscalationRates>;
  acceptedShareToday: PulseFactSection<PulseAcceptedShare[]>;
  locationHealth7d: PulseFactSection<PulseLocationHealthSummary>;
  /** Conversations in the product's "Struggling" band (score below 40) in the last 24 h, per location. */
  struggling24h: PulseFactSection<PulseStrugglingAtLocation[]>;
  /** Conversations by hour and day, by topic, and health by week — for the digest. */
  activity: PulseFactSection<PulseActivity>;
  /** The org's groups and their locations, for the Regional Manager's roll-ups (ENG-3225). */
  groups: PulseFactSection<PulseGroupFact[]>;
  /** Health per location per local week, the last 12 weeks with this one (ENG-3225). */
  locationWeeklyHealth12w: PulseFactSection<PulseLocationHealthWeeks>;
  /** Reviews per location, platform and local week, the last 13 weeks with this one (ENG-3225). */
  reviewWeeks13w: PulseFactSection<PulseReviewWeeks>;
  /** Leads per location and local week, the last 13 weeks with this one (ENG-3225). */
  leadWeeks13w: PulseFactSection<PulseLeadWeeks>;
  /** The org's latest escalation themes, at most 14 days old; null when there are none (ENG-3225). */
  escalationThemes: PulseFactSection<PulseEscalationThemes | null>;
}

// ─── Member facts ───────────────────────────────────────────────────────────

export interface PulseMemberIdentity {
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  isSwivlAdmin: boolean;
}

export interface PulsePendingOffer {
  assignmentId: number;
  botConversationId: number;
  channel: PulseChannel;
  offeredAt: string;
  expiresAt: string;
}

export interface PulseTicketRow {
  id: number;
  number: number;
  title: string;
  status: string;
  priority: string;
  dueDate: string | null;
  locationId: number | null;
  createdAt: string;
}

export interface PulseAfterHoursAtLocation {
  /** Null for conversations with no Contact Center location. */
  locationId: number | null;
  afterHours: number;
}

export interface PulsePendingOffers {
  offers: PulsePendingOffer[];
  /** Offers that timed out unanswered in the last 15 minutes. */
  missedLast15m: number;
}

/**
 * Ticketing's "My inbox" assigned lane: tickets the member claimed or is the
 * sole assignee of. `total` uses Ticketing's own default filter; `overdue`
 * counts open tickets past their due date; `overdueRows` lists them, oldest due first.
 */
export interface PulseMyTickets {
  total: number;
  overdue: number;
  overdueRows: PulseTicketRow[];
  /** The lane's first tickets (PULSE_MY_WORK_ROWS), in Ticketing's own order — what My Work orders. */
  rows: PulseTicketRow[];
}

/** The Agent Dashboard's "today", which is a trailing 24 hours. */
export interface PulseAgentLast24h {
  offered: number;
  accepted: number;
  completed: number;
  avgHandleSeconds: number | null;
  activeTakeovers: number;
}

/** Mean health of the member's ended, scored conversations: today (org-local) vs the 30 days before, and this month so far. */
export interface PulseMemberHealth {
  today: PulseMeasure;
  baseline30d: PulseMeasure;
  monthToDate: PulseMeasure;
}

/** Conversations the member accepted this month so far. */
export interface PulseAgentMonthToDate {
  accepted: number;
}

/** Conversations the member accepted in a window (the last 7 days, or this month), and how many were after hours in the location's own timezone. */
export interface PulseAfterHoursLoad {
  accepted: number;
  afterHours: number;
  byLocation: PulseAfterHoursAtLocation[];
}

export type PulseEscalationEligibility = "all" | "tickets-only" | "none";

export interface PulseMemberFacts {
  orgId: number;
  memberId: number;
  builtAt: string;
  identity: PulseMemberIdentity;
  reach: PulseFactSection<PulseReach>;
  /** Strongest escalation eligibility across the member's roles (display gate, not authorization). */
  canTakeEscalations: PulseFactSection<PulseEscalationEligibility>;
  pendingOffers: PulseFactSection<PulsePendingOffers>;
  myTickets: PulseFactSection<PulseMyTickets>;
  agentLast24h: PulseFactSection<PulseAgentLast24h>;
  memberHealth: PulseFactSection<PulseMemberHealth>;
  afterHours7d: PulseFactSection<PulseAfterHoursLoad>;
  agentMonthToDate: PulseFactSection<PulseAgentMonthToDate>;
  afterHoursMonthToDate: PulseFactSection<PulseAfterHoursLoad>;
}
